import Card from "components/card";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { IoEye } from "react-icons/io5";
import { useNavigate, useSearchParams } from "react-router-dom";
import UserService from "services/user.service";
import Pagination from "components/pagination/Pagination";
import Modal from "components/modal/Modal";
import Profile from "./Profile";
import moment from "moment";
import Loader from "../../../assets/img/loader.gif";
import Avatar from "react-avatar";
import { FiSearch } from "react-icons/fi";
import Toast from "components/toaster";
import { includes } from "lodash";
import { BsChevronDown } from "react-icons/bs";
import Organization from "assets/img/organization.png";
import Personal from "assets/img/personal.png";
import Select from "react-select";
import { PiCrownSimpleFill } from "react-icons/pi";
import { FaTrash } from "react-icons/fa6";
import { MdAdminPanelSettings, MdDeleteForever, MdPersonOff, MdStorefront } from "react-icons/md";
import tabCache from "utils/tabCache";

const columnHelper = createColumnHelper();

/** Last row when not searching: open above trigger. While searching, keep below (default). */
function rowMenuAnchorFromTriggerRect(rect, openAbove) {
  if (openAbove) {
    return {
      placement: "above",
      triggerTop: rect.top,
      left: rect.left,
      width: rect.width,
    };
  }
  return {
    placement: "below",
    top: rect.bottom,
    left: rect.left,
    width: rect.width,
  };
}

function getRowMenuPortalStyle(anchor) {
  if (!anchor) return {};
  if (anchor.placement === "above") {
    const h = typeof window !== "undefined" ? window.innerHeight : 0;
    return {
      left: anchor.left,
      width: anchor.width,
      bottom: h - anchor.triggerTop + 4,
      top: "auto",
    };
  }
  return {
    top: anchor.top + 4,
    left: anchor.left,
    width: anchor.width,
  };
}

// Profile Image Component with error handling
const ProfileImage = ({ src, alt, email, name, username, className }) => {
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);

  useEffect(() => {
    if (src) {
      const fullSrc = src?.includes("http")
        ? src
        : process.env.REACT_APP_BASE_URL + src;
      setImageSrc(fullSrc);
      setImageError(false);
    } else {
      setImageError(true);
    }
  }, [src]);

  const handleImageError = (e) => {
    // Suppress console errors and prevent broken image icon
    e.target.style.display = 'none';
    setImageError(true);
  };

  if (imageError || !imageSrc) {
    return <Avatar name={email || name || username || "U"} className={className} round={false} />;
  }

  return (
    <img
      src={imageSrc}
      alt={alt || "Profile"}
      className={className}
      onError={handleImageError}
      onLoad={(e) => {
        // Ensure image is visible when it loads successfully
        e.target.style.display = '';
      }}
    />
  );
};

const Marketplace = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startDateParam = searchParams.get("startDate");
  const endDateParam = searchParams.get("endDate");

  const dateRangeFromParams = React.useMemo(() => {
    if (!startDateParam || !endDateParam) {
      return null;
    }

    const start = new Date(startDateParam);
    const end = new Date(endDateParam);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }

    const normalizedStart = new Date(start);
    normalizedStart.setHours(0, 0, 0, 0);

    const normalizedEnd = new Date(end);
    normalizedEnd.setHours(23, 59, 59, 999);

    return {
      startDate: normalizedStart.toISOString(),
      endDate: normalizedEnd.toISOString(),
    };
  }, [startDateParam, endDateParam]);
  const activeMenuPortalRef = useRef(null);
  const proMenuPortalRef = useRef(null);
  const profileRef = useRef();
  const activateRef = useRef();
  const deleteRef = useRef();
  const [sorting, setSorting] = React.useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupOpenPro, setPopupOpenPro] = useState(false);
  const [profilePopup, setProfilePopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  // Initialize from cache - show loader only on first load
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState("");
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [reportType, setReportType] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [proDropdown, setProDropdown] = useState(null);
  const [activeMenuAnchor, setActiveMenuAnchor] = useState(null);
  const [proMenuAnchor, setProMenuAnchor] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [popupOpenDelete, setPopupOpenDelete] = useState(false);

  const [isPageChanging, setIsPageChanging] = useState(false);


  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  const colourStyles = {
    option: (styles, state) => ({
      ...styles,
      backgroundColor: "rgb(30 30 32 / 1) !important",
      color: "#ffffff",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "rgba(255, 255, 255, 0.30) !important",
        color: "#ffffff",
      },
    }),
    control: (styles, { isFocused }) => ({
      ...styles,
      backgroundColor: "rgb(12 14 28 / 1)",
      borderRadius: "9999px",
      borderColor: "rgba(255, 255, 255, 0.25)",
      boxShadow: "none",
      outline: "none",
      paddingLeft: "8px",
      ...(isFocused
        ? {
            borderColor: "rgba(255, 255, 255, 0.35)",
          }
        : {}),
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "#ffffff",
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: "rgb(12 14 28 / 1)",
      borderRadius: "18px",
      overflow: "hidden",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  // Create debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchText(value);
    }, 500),
    []
  );

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  const handleOpenDelete = async (id, active, data) => {
    setSelectedUser(data);
    setId(id);
    setPopupOpenDelete(true);
    document.body.style.overflow = "hidden";
  };

  const columns = [
    columnHelper.accessor("accountDetails", {
      id: "profile",
      header: () => (
        <p className="text-sm font-bold text-left ml-7 uppercase text-white">Profile</p>
      ),
      cell: (info) => {
        const email = info.row.original.accountDetails.email;
        const name = info.row.original.accountDetails.name;
        const username = info.row.original.accountDetails.username;
        const displayName = name || username || "--";
        const profilepic = info.row.original.accountDetails.profilepic;
        const account_type = info.row.original.accountDetails.is_organization;
        const isPrime = !!info.row.original?.is_prime_user;
        const isVendor = !!(
          info.row.original?.is_vendor ?? info.row.original?.accountDetails?.is_vendor
        );
        const isGuest = !!(
          info.row.original?.guest_user ??
          info.row.original?.is_guest_user ??
          info.row.original?.accountDetails?.guest_user ??
          info.row.original?.accountDetails?.is_guest_user
        );
        return (
          <div className="flex items-center gap-3">
            <div className="relative">
              <ProfileImage
                src={profilepic}
                alt="Profile"
                email={email}
                name={name}
                username={username}
                className="!h-8 !w-8 rounded-lg object-cover"
              />
              {info.row.original?.is_prime_user && (
                <div className="absolute -bottom-1 -right-1 rounded-full bg-primary p-0.5 border-[1px] !border-navy-800">
                  <PiCrownSimpleFill size={10} />
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <p >{displayName}</p>
              <div className="flex mt-1 items-center gap-4">
                {/* Account type + inline "Type" icons (same idea as the Type column) */}
                <div className="flex items-center gap-2 ">
                  <img
                    src={account_type !== true ? Personal : Organization}
                    alt="Account Type"
                    className="!h-3 !w-3 object-cover"
                    title={account_type === true ? "Organization" : "Personal"}
                  />

                  {/* <div
                    className={`flex h-5 w-5 items-center justify-center rounded border border-white/10 ${
                      isPrime
                        ? "bg-primary/20 text-primary"
                        : "bg-[#369FFF]/10 text-[#369FFF]/100"
                    }`}
                    title={isPrime ? "Premium" : "Free"}
                    aria-label={isPrime ? "Premium user" : "Free user"}
                  >
                    <PiCrownSimpleFill size={11} />
                  </div> */}

                  {isVendor && (
                    <div
                      className="flex  border-white/10  text-[#A54BF5]/100"
                      title="Vendor"
                      aria-label="Vendor"
                    >
                      <MdStorefront size={14} />
                    </div>
                  )}

                  {isGuest && (
                    <div
                      className="flex  items-center justify-center rounded border border-white/10  text-gray-200"
                      title="Guest User"
                      aria-label="Guest user"
                    >
                      <MdPersonOff size={12} />
                    </div>
                  )}
                </div>
                
                {/* <p
                  className={`text-xs capitalize ${account_type !== true ? "text-green-600" : "text-primary"
                    }`}
                >
                  {account_type !== true ? "Personal" : "Organization"}
                  {account_type === true && (
                    <div className="flex items-center gap-2">
                      <PiCrownSimpleFill size={10} />
                    </div>
                  )}
                </p> */}
              </div>
            </div>
          </div>
        );
      },
    }),
    // New column: show Premium/Free + Personal/Organization via icons
    // columnHelper.accessor((row) => row, {
    //   id: "userTypeIcons",
    //   header: () => (
    //     <p className="text-sm font-bold uppercase text-left xs:ml-28 text-white whitespace-nowrap">
    //       Type
    //     </p>
    //   ),
    //   cell: (info) => {
    //     const isPrime = !!info.row.original?.is_prime_user;
    //     const isOrg = !!info.row.original?.accountDetails?.is_organization;
    //     const isAdmin = !!(
    //       info.row.original?.is_admin ?? info.row.original?.accountDetails?.is_admin
    //     );
    //     const isVendor = !!(
    //       info.row.original?.is_vendor ?? info.row.original?.accountDetails?.is_vendor
    //     );
    //     const isGuest = !!(
    //       info.row.original?.guest_user ??
    //       info.row.original?.is_guest_user ??
    //       info.row.original?.accountDetails?.guest_user ??
    //       info.row.original?.accountDetails?.is_guest_user
    //     );
        
        
    //     return (
    //       <div className="flex items-center gap-2 ml-4 ">
    //         <div
    //           className={`flex h-7 w-7 items-center justify-center rounded-md border border-white/10 ${isPrime ? "bg-primary/20 text-primary" : "bg-[#369FFF]/10 text-[#369FFF]/100"
    //             }`}
    //           title={isPrime ? "Premium" : "Free"}
    //           aria-label={isPrime ? "Premium user" : "Free user"}
    //         >
    //           <PiCrownSimpleFill size={14} />
    //         </div>

    //         <div
    //           className={`flex h-7 w-7 items-center justify-center rounded-md border border-white/10 ${isOrg ? "bg-primary/10" : "bg-green-500/10"
    //             }`}
    //           title={isOrg ? "Organization" : "Personal"}
    //           aria-label={isOrg ? "Organization account" : "Personal account"}
    //         >
    //           <img
    //             src={isOrg ? Organization : Personal}
    //             alt={isOrg ? "Organization" : "Personal"}
    //             className="h-4 w-4 object-contain"
    //           />
    //         </div>

    //         {/* {isAdmin && (
    //           <div
    //             className="flex h-7 w-7 items-center justify-center rounded-md border border-white/10 bg-purple-500/10 text-purple-300"
    //             title="Admin"
    //             aria-label="Admin"
    //           >
    //             <MdAdminPanelSettings size={16} />
    //           </div>
    //         )} */}

    //         {isVendor && (
    //           <div
    //             className="flex h-7 w-7 items-center justify-center bg-[#A54BF5]/20  text-[#A54BF5]/100 rounded-md border border-white/10 "
    //             title="Vendor"
    //             aria-label="Vendor"
    //           >
    //             <MdStorefront size={16} />
    //           </div>
    //         )}

    //         {isGuest && (
    //           <div
    //             className="flex h-7 w-7 items-center justify-center rounded-md border border-white/10 bg-gray-500/10 text-gray-200"
    //             title="Guest User"
    //             aria-label="Guest user"
    //           >
    //             <MdPersonOff size={16} />
    //           </div>
    //         )}
    //       </div>
    //     );
    //   },
    // }),
    columnHelper.accessor("accountDetails", {
      id: "email",
      header: () => (
        <p className="text-sm font-bold uppercase text-left  xs:ml-28 text-white">Email</p>
      ),
      cell: (info) => (
        <div className="flex items-center ml-4">
          {info.getValue()?.email ? info.getValue()?.email : "--"}
          {/* <Progress width="w-[108px]" value={info.getValue()} /> */}
        </div>
      ),
    }),
    columnHelper.accessor("accountDetails", {
      id: "mobileNumber",
      header: () => (
        <p className="text-sm font-bold uppercase items-left whitespace-nowrap xs:ml-32 2xl:ml-20  mobile-number-header-1200-1900 text-white">Mobile Number</p>
      ),
      cell: (info) => {
        const phone = info.getValue()?.phone;
        const hasPhone = phone && phone.trim() !== "";
        return (
          <div className="flex items-center ">
            {/* {info.getValue() === "Approved" ? (
            <MdCheckCircle className="me-1 text-green-300" />
          ) : info.getValue() === "Disable" ? (
            <MdCancel className="me-1 text-red-300" />
          ) : info.getValue() === "Error" ? (
            <MdOutlineError className="me-1 text-amber-300" />
          ) : null} */}
            <p className={`text-sm text-white ${hasPhone ? "ml-8 " : "ml-16"}`}>
              {hasPhone
                ? phone.includes("+")
                  ? phone
                  : "+" + phone
                : "--"}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor("is_active", {
      id: "is_active",
      header: () => (
        <p className="text-sm ml-48 xl:ml-48 2xl:ml-10 action-header-1300-1900  font-bold uppercase text-white">Action</p>
      ),
      cell: (info) => {
        const rowId = info.row.original._id;
        const primeUser = info.row.original.is_prime_user;
        const rowCount = info.table.getRowModel().rows.length;
        const rowIndex = info.row.index;
        const hasSearch = searchText.trim().length > 0;
        const isLastRow =
          rowCount > 0 && rowIndex === rowCount - 1;
        let openMenuAbove;
        if (hasSearch && rowCount === 1) {
          openMenuAbove = false;
        } else if (rowCount > 1) {
          openMenuAbove = isLastRow;
        } else {
          openMenuAbove = true;
        }
        return (
          <div className="flex gap-3">
            <div
              className="ms-center flex h-7 w-7 cursor-pointer items-center justify-center rounded-md bg-[rgba(255,37,37,0.1)] text-[rgba(255,44,57,1)]  opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() =>
                handleOpenDelete(rowId, info.getValue(), info.row.original)
              }
            >
              <MdDeleteForever size={18} />
            </div>

            <div className="relative">
              <div
                data-table-action-active-trigger
                className={`ms-center w-28 h-8 m-0 inline-flex  items-center justify-between rounded-md px-3 py-1 text-xs font-bold cursor-pointer ${info.getValue()
                    ? "bg-[#15290E]/55 text-[#38B600] "
                    : "bg-[rgba(255,37,37,0.1)] text-[#f06670d5]"

                  }`}
                onClick={(e) => {
                  if (activeDropdown === rowId) {
                    setActiveDropdown(null);
                    setActiveMenuAnchor(null);
                  } else {
                    const r = e.currentTarget.getBoundingClientRect();
                    setActiveMenuAnchor(
                      rowMenuAnchorFromTriggerRect(r, openMenuAbove)
                    );
                    setProDropdown(null);
                    setProMenuAnchor(null);
                    setActiveDropdown(rowId);
                  }
                }}
              >
                <span className="text-left">
                  {info.getValue() ? "Active" : "In Active"}
                </span>
                <BsChevronDown className="h-2 w-2 " style={{ strokeWidth: '2' }} />
              </div>
            </div>

            <div className="relative">
              <div
                data-table-action-pro-trigger
                className={`ms-center w-28 h-8 m-0 inline-flex items-center justify-between rounded-md px-3 py-1 text-xs font-bold cursor-pointer ${primeUser
                    ? "bg-[#FF80001A]/10 text-[rgba(255,128,0,1)] "
                    : "bg-[#369FFF]/10 text-[#369FFF]/100"
                  }`}
                onClick={(e) => {
                  if (proDropdown === rowId) {
                    setProDropdown(null);
                    setProMenuAnchor(null);
                  } else {
                    const r = e.currentTarget.getBoundingClientRect();
                    setProMenuAnchor(
                      rowMenuAnchorFromTriggerRect(r, openMenuAbove)
                    );
                    setActiveDropdown(null);
                    setActiveMenuAnchor(null);
                    setProDropdown(rowId);
                  }
                }}
              >
                <span className="text-left">
                  {primeUser ? "Premium" : "Free"}
                </span>
                <BsChevronDown className="h-2 w-2" style={{ strokeWidth: '2' }} />
              </div>
            </div>
          </div>
        );
      },
    }),
  ];

  const prevSearchTextRef = useRef("");
  const [profileData, setProfileData] = useState([]);
  // Initialize from cache if available
  const [data, setData] = React.useState(tabCache.users.data || []);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const handleOpenAvtive = async (id, active) => {
    setSelectedUser(active);
    setId(id);
    setPopupOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleOpenPro = async (id, active) => {
    console.log("🚀 ~ handleOpenPro ~ active:", active);
    setSelectedUser(active);
    setId(id);
    setPopupOpenPro(true);
    document.body.style.overflow = "hidden";
  };

  const getUser = async (isSearching = false) => {
    const isFirstLoad = tabCache.users.firstLoad;

    // Show loader on first load or when searching
    if (isFirstLoad || isSearching || isPageChanging) {
      setIsLoading(true);
    }


    try {
      const userType = searchParams.get("user_type");
      const filters = {};

      // Add user_type filter if present in URL (proActive, normalActive, organizationActive)
      if (userType) {
        filters.user_type = userType;
      }

      // Add is_active filter if reportType is set
      if (reportType !== null) {
        filters.is_active = reportType;
      }

      if (dateRangeFromParams) {
        filters.startDate = dateRangeFromParams.startDate;
        filters.endDate = dateRangeFromParams.endDate;
      }

      const body = {
        pagination: {
          page: page,
          pageSize: pageSize,
          projection: {},
          filters: filters,
          sort: {},
        },
        searchtext: encodeURIComponent(searchText),

        ...(dateRangeFromParams
          ? {
            startDate: dateRangeFromParams.startDate,
            endDate: dateRangeFromParams.endDate,
          }
          : {}),
      };
      const response = await UserService.getAllUser(body);
      const usersData = response.data.data;

      // Update state and cache
      setData(usersData);
      tabCache.users.data = usersData;
      setTotalPages(Math.ceil(response.data.totalCount / pageSize));

      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.users.firstLoad = false;
      }
    } catch (error) {
      Toast("error", error.response.data.message);
      const emptyData = [];
      setData(emptyData);
      tabCache.users.data = emptyData;
      if (isFirstLoad) {
        tabCache.users.firstLoad = false;
      }
    } finally {
      if (isFirstLoad || isSearching || isPageChanging) {
        setIsLoading(false);
      }
      setIsPageChanging(false); // 👈 STOP pagination loader
    }


  };

  useEffect(() => {
    // Reset page to 1 when filters change
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    // Check if searchText changed (this indicates a search operation)
    const isSearchChange = prevSearchTextRef.current !== searchText;
    if (isSearchChange) {
      prevSearchTextRef.current = searchText;
    }

    // If we have cached data, show it immediately and fetch in background
    if (tabCache.users.data) {
      // Data already shown from initial state, just fetch in background
      // Show loader if this is a search operation
      getUser(isSearchChange);
    } else {
      // No cached data, fetch and show loader
      getUser(false);
    }
    // eslint-disable-next-line
  }, [page, pageSize, searchText, reportType, searchParams]);

  const handlePageChange = (newPage) => {
    setIsPageChanging(true);   // 👈 START loader for pagination
    setPage(newPage);
  };


  const handleToggleActivation = async () => {
    setConfirmLoader(true);
    try {
      const body = {
        user_id: id,
        is_active: !selectedUser,
      };
      const response = await UserService.updateUserDetails(body);
      await getUser();
      setPopupOpen(false);
      document.body.style.overflow = "unset";
      setActiveDropdown(null);
      setActiveMenuAnchor(null);
      Toast("success", response.data.message);
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setConfirmLoader(false);
    }
  };

  const handleTogglePro = async () => {
    setConfirmLoader(true);
    try {
      const body = {
        user_id: id,
        is_prime: selectedUser,
      };
      console.log("🚀 ~ handleTogglePro ~ body:", body);
      const response = await UserService.updateUserPro(body);
      await getUser();
      setPopupOpenPro(false);
      document.body.style.overflow = "unset";
      setProDropdown(null);
      setProMenuAnchor(null);
      Toast("success", response.data.message);
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setConfirmLoader(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (confirmLoader) return;
      if (activeDropdown || proDropdown) {
        const t = event.target;
        const insideRowMenu =
          activeMenuPortalRef.current?.contains(t) ||
          proMenuPortalRef.current?.contains(t) ||
          t.closest("[data-table-action-active-trigger]") ||
          t.closest("[data-table-action-pro-trigger]");
        if (!insideRowMenu) {
          setActiveDropdown(null);
          setProDropdown(null);
          setActiveMenuAnchor(null);
          setProMenuAnchor(null);
        }
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfilePopup(false);
      }
      if (activateRef.current && !activateRef.current.contains(event.target)) {
        setPopupOpen(false);
        setPopupOpenPro(false);
      }
      if (deleteRef.current && !deleteRef.current.contains(event.target)) {
        setPopupOpenDelete(false);
      }
      document.body.style.overflow = "unset";
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [confirmLoader, activeDropdown, proDropdown]);

  useEffect(() => {
    if (!activeDropdown && !proDropdown) return;
    const closeRowMenus = () => {
      setActiveDropdown(null);
      setProDropdown(null);
      setActiveMenuAnchor(null);
      setProMenuAnchor(null);
    };
    window.addEventListener("scroll", closeRowMenus, true);
    window.addEventListener("resize", closeRowMenus);
    return () => {
      window.removeEventListener("scroll", closeRowMenus, true);
      window.removeEventListener("resize", closeRowMenus);
    };
  }, [activeDropdown, proDropdown]);

  const options = [
    { label: "All", value: null },
    { label: "Active", value: true },
    { label: "In Active", value: false },
  ];


  const handleSelectReportType = async (option) => {
    setReportType(option.value);
  };

  const handleDelete = async () => {
    setConfirmLoader(true);
    try {
      const body = {
        user_id: id,
      };
      const response = await UserService.deleteUser(body);
      await getUser();
      setPopupOpenDelete(false);
      document.body.style.overflow = "unset";
      Toast("success", response.data.message);
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setConfirmLoader(false);
    }
  };

  const activeRowForMenu =
    activeDropdown && data.find((u) => u._id === activeDropdown);
  const proRowForMenu =
    proDropdown && data.find((u) => u._id === proDropdown);

  return (
    <>
      <style>
        {`
          @media (min-width: 1300px) and (max-width: 1860px) {
            .action-header-1300-1900 {
              margin-left: 12rem;
            }
          }
          @media (min-width: 1200px) and (max-width: 1600px) {
            .mobile-number-header-1200-1900 {
              margin-left: 8rem;
            }
          }
        `}
      </style>
      <div className="mt-3 grid h-full grid-cols-1 gap-5">
        <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
          {/* <ComplexTable
          columnsData={columnsDataComplex}
          tableData={tableDataComplex}
        /> */}

          <Card extra={"w-full h-full px-6 pb-6 overflow-x-auto"}>
            {/* <div className="relative flex items-center justify-between pt-4">
            <div className="text-xl font-bold text-white">Users</div>
          </div> */}
            <div className="flex w-full flex-col items-start justify-start gap-3 sm:flex-row xs:flex-row sm:items-center sm:justify-between xxs:mb-8">
              <div className="mt-4 -ml-2 flex justify-start xxs:w-auto xs:w-auto sm:w-auto">
                <div className="flex items-center rounded-full bg-navy-900 py-3 text-white xl:w-[180px] xxs:w-[180px] xs:w-[180px] sm:w-auto">
                  <p className="pl-3 pr-2 text-xl">
                    <FiSearch className="h-4 w-4 text-white" />
                  </p>
                  <input
                    type="text"
                    placeholder="Search..."
                    className="block w-full bg-navy-900 text-sm font-medium max-w-[120px]  text-white outline-none placeholder:!text-white sm:w-fit"
                    onChange={handleSearchInputChange}
                    value={searchInputValue}
                  />
                </div>
              </div>

              <div className="default sm:-mt-1  sm:mb-0 mb-4  me-2  flex h-full w-full items-center justify-start xxs:w-full xs:w-full sm:w-[280px] sm:justify-end xs:ml-12  sm:-mr-3 xs:-mt-1 sm:self-auto md:-mt-3 lg:-mt-2 2xl:-mt-2 [@media(max-width:375px)]:mb-14 [@media(max-width:320px)]:mb-4 ">
                <Select
                  className="ms-0 h-4 w-[150px] !text-white xxs:ms-0 xs:ms-0 sm:ms-4"
                  classNamePrefix="select"
                  defaultValue={options[0]}
                  name="color"
                  options={options}
                  value={options.find((option) => option?.value == reportType)}
                  onChange={handleSelectReportType}
                  styles={colourStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  menuPlacement="bottom"
                  menuShouldScrollIntoView={false}
                />
              </div>
            </div>
            <div
              className={`table-scroll -mt-6 -mx-6 relative ${isLoading
                  ? "xxs:h-[50vh] xs:h-[70vh] sm:h-[65vh] overflow-hidden"
                  : "overflow-x-auto"
                }`}
            >
              {isLoading && (
                <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center">
                  <img src={Loader} alt="Loading..." className="h-16 w-16" />
                </div>
              )}
              <table
                className={`w-full ${isLoading ? "table-fixed" : ""}`}
                style={
                  isLoading
                    ? { tableLayout: "fixed", width: "100%" }
                    : { tableLayout: "auto", minWidth: "100%" }
                }
              >
                <thead className={isLoading ? "hidden" : ""}>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr
                      key={headerGroup.id}
                      className="!border-px !border-gray-400"
                    >
                      {headerGroup.headers.map((header) => {
                        return (
                          <th
                            key={header.id}
                            colSpan={header.colSpan}
                            onClick={header.column.getToggleSortingHandler()}
                            className="cursor-pointerv border-b-[1px] border-[#fbfbfb1a]  pb-2 pr-24 pt-4 text-center"
                          >
                            <div className="items-center justify-between text-xs text-gray-200">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: "",
                                desc: "",
                              }[header.column.getIsSorted()] ?? null}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {!isLoading && table.getRowModel().rows.length > 0 ? (
                    table.getRowModel().rows.map((row) => {
                      return (
                        <tr
                          key={row.id}
                          className="group border-b border-[#fbfbfb1a] last:border-none hover:bg-[#8E8E8E]/5 hover:!border-b-0 transition-colors cursor-pointer"
                        >
                          {row.getVisibleCells().map((cell, cellIndex) => {
                            const isFirst = cellIndex === 0;
                            const isLast = cellIndex === row.getVisibleCells().length - 1;
                            // Email (1), Mobile Number (2), Action (3) columns should be shifted right
                            const shouldShiftRight = cellIndex === 1 || cellIndex === 2 || cellIndex === 3;
                            return (
                              <td
                                key={cell.id}
                                className={`min-w-[150px] border-white/0 py-3 pr-4 ${isFirst ? 'pl-6' : ''} ${isLast ? 'pr-6' : ''} ${shouldShiftRight ? 'pl-24' : ''}`}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  ) : !isLoading ? (
                    <tr>
                      <td colSpan={columns.length} className="py-4 text-center">
                        <p className="my-3">Data Not Found</p>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {activeDropdown &&
              activeMenuAnchor &&
              activeRowForMenu &&
              createPortal(
                <div
                  ref={activeMenuPortalRef}
                  className="fixed z-[10000] rounded-md border border-white/10 bg-navy-800 px-1.5 py-1.5 shadow-[0_0_10px_rgba(255,255,255,0.15)]"
                  style={getRowMenuPortalStyle(activeMenuAnchor)}
                >
                  <button
                    type="button"
                    className="block w-full rounded-md px-4 py-1.5 text-left text-xs font-bold text-white transition-colors hover:bg-white/15"
                    onClick={() =>
                      handleOpenAvtive(
                        activeDropdown,
                        activeRowForMenu.is_active
                      )
                    }
                  >
                    {activeRowForMenu.is_active ? "Deactivated" : "Active"}
                  </button>
                </div>,
                document.body
              )}

            {proDropdown &&
              proMenuAnchor &&
              proRowForMenu &&
              createPortal(
                <div
                  ref={proMenuPortalRef}
                  className="fixed z-[10000] rounded-md border border-white/10 bg-navy-800 px-1.5 py-1.5 shadow-[0_0_10px_rgba(255,255,255,0.2)]"
                  style={getRowMenuPortalStyle(proMenuAnchor)}
                >
                  {proRowForMenu.is_prime_user ? (
                    <button
                      type="button"
                      className="block w-full rounded-md px-3 py-1.5 text-left text-xs font-bold text-white transition-colors hover:bg-white/10"
                      onClick={() => handleOpenPro(proDropdown, 0)}
                    >
                      Free
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="block w-full rounded-md px-3 py-1.5 text-left text-xs font-bold text-white transition-colors hover:bg-white/10"
                      onClick={() => handleOpenPro(proDropdown, 1)}
                    >
                      Premium
                    </button>
                  )}
                </div>,
                document.body
              )}

            {table.getRowModel().rows.length > 0 && !isLoading && (
              <Pagination
                page={page}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
              />
            )}

            <Modal
              isOpen={popupOpen}
              onClose={() => {
                setPopupOpen(false);
                document.body.style.overflow = "unset";
              }}
              isActive={selectedUser}
              onToggle={handleToggleActivation}
              activateRef={activateRef}
              confirmLoader={confirmLoader}
            />

            <Modal
              isOpen={popupOpenPro}
              onClose={() => {
                setPopupOpenPro(false);
                document.body.style.overflow = "unset";
              }}
              isActive={selectedUser}
              onToggle={handleTogglePro}
              activateRef={activateRef}
              confirmLoader={confirmLoader}
            />

            <Profile
              profileRef={profileRef}
              profileData={profileData}
              isOpen={profilePopup}
              onClose={() => {
                setProfilePopup(false);
                document.body.style.overflow = "unset";
              }}
            />

            {popupOpenDelete && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                <div
                  className="w-96 rounded-xl bg-navy-800 p-6 shadow-lg"
                  ref={deleteRef}
                >
                  <h2 className="mb-4 text-xl font-bold text-white">Delete</h2>
                  <p className="mb-6 text-gray-600">
                    Are you sure you want to delete this user?
                  </p>
                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (confirmLoader) return;
                        setPopupOpenDelete(false);
                        document.body.style.overflow = "unset";
                      }}
                      disabled={confirmLoader}
                      className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20 disabled:pointer-events-none disabled:opacity-40"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleDelete();
                      }}
                      disabled={confirmLoader}
                      className="w-24 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90 disabled:opacity-90"
                    >
                      {confirmLoader ? (
                        <img
                          src={Loader}
                          alt="Loading..."
                          className="mx-auto h-6 w-6"
                        />
                      ) : (
                        "Delete"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
};

export default Marketplace;
